import React, { useState, useEffect, useMemo } from 'react';
import { ShoppingBag, Plus, Edit, Trash2, X, Type, AlignLeft, DollarSign, Download, BookOpen, Users, Gift, Link, Search, Filter, Copy, Star, GripVertical, Tag, Hash, AlertTriangle, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import { getAllProducts, createProduct, updateProduct, deleteProduct, reorderProducts } from '../firebase/products';
import { getAllCourses, getCourse } from '../firebase/courses';
import { CardSkeleton } from './LoadingSkeleton';
import { ImageUpload } from './ImageUpload';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Delete Confirmation Modal
function DeleteConfirmModal({ product, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4 sm:p-4" onClick={onCancel}>
      <div 
        className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6 shadow-xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="text-red-600" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Product</h3>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </div>
        </div>
        
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-700">
            Are you sure you want to delete <strong>"{product?.title}"</strong>?
          </p>
          <p className="text-xs text-gray-500 mt-2">
            This will permanently remove the product from your store. Any associated orders will remain, but the product will no longer be available for purchase.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={18} />
                Delete Product
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sortable Product Card
function SortableProductCard({ product, onEdit, onDelete, onDuplicate, onToggleStatus, isReordering }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden transition-all duration-200 ${
        isDragging ? 'shadow-2xl scale-105 rotate-2' : 'hover:shadow-lg hover:border-[#c5a059]'
      } ${product.featured ? 'ring-2 ring-[#c5a059] ring-opacity-30' : ''}`}
    >
      {product.thumbnail && (
        <div className="relative">
          <img src={product.thumbnail} alt={product.title} className="w-full h-48 object-cover" />
          {product.featured && (
            <div className="absolute top-2 right-2 p-1.5 bg-[#c5a059] rounded-full shadow-lg">
              <Star size={16} className="text-white fill-white" />
            </div>
          )}
        </div>
      )}
      {!product.thumbnail && product.featured && (
        <div className="absolute top-2 right-2 p-1.5 bg-[#c5a059] rounded-full shadow-lg z-10">
          <Star size={16} className="text-white fill-white" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-800 line-clamp-1">{product.title}</h3>
            </div>
            {product.category && (
              <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded mb-1">
                {product.category}
              </span>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
              product.status === 'published' ? 'bg-green-100 text-green-800' : 
              product.status === 'archived' ? 'bg-gray-100 text-gray-600' : 
              'bg-yellow-100 text-yellow-800'
            }`}>
              {product.status}
            </span>
            <button
              onClick={() => onToggleStatus(product)}
              className="p-1 text-gray-400 hover:text-[#c5a059] transition"
              title={product.status === 'published' ? 'Unpublish' : 'Publish'}
            >
              {product.status === 'published' ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {product.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">
                {tag}
              </span>
            ))}
            {product.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-gray-500">+{product.tags.length - 3}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <p className="text-lg font-bold text-[#c5a059]">
            {(() => {
              const price = parseFloat(product.price) || 0;
              return isNaN(price) || price === 0 ? 'Free' : `£${price.toFixed(2)}`;
            })()}
          </p>
          <div className="flex gap-1">
            {!isReordering && (
              <button
                {...attributes}
                {...listeners}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition cursor-grab active:cursor-grabbing"
                title="Drag to reorder"
                aria-label="Drag to reorder"
              >
                <GripVertical size={18} />
              </button>
            )}
            <button
              onClick={() => onDuplicate(product)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition button-press"
              title="Duplicate product"
              aria-label="Duplicate"
            >
              <Copy size={18} />
            </button>
            <button
              onClick={() => onEdit(product)}
              className="p-2 text-gray-400 hover:text-[#c5a059] hover:bg-gray-50 rounded-lg transition button-press"
              title="Edit product"
              aria-label="Edit"
            >
              <Edit size={18} />
            </button>
            <button
              onClick={() => onDelete(product)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition button-press"
              title="Delete product"
              aria-label="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminProductsTab({ products, setProducts, showToast, loading, setLoading }) {
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('order');
  const [courses, setCourses] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadProducts();
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourses = async () => {
    try {
      const coursesData = await getAllCourses();
      setCourses(coursesData || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const allProducts = await getAllProducts();
      setProducts(allProducts);
    } catch (error) {
      showToast('Error loading products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(p => p.type === filterType);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'order':
          return (a.order || 0) - (b.order || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'price':
          return (b.price || 0) - (a.price || 0);
        case 'created':
          return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, searchQuery, filterType, filterStatus, sortBy]);

  const handleSaveProduct = async (productData) => {
    setLoading(true);
    try {
      let result;
      if (editingProduct) {
        result = await updateProduct(editingProduct.id, productData);
      } else {
        result = await createProduct(productData);
      }
      
      if (result.success) {
        showToast(`Product ${editingProduct ? 'updated' : 'created'} successfully!`, 'success');
        setShowProductModal(false);
        setEditingProduct(null);
        loadProducts();
      } else {
        showToast(`Error ${editingProduct ? 'updating' : 'creating'} product: ${result.error}`, 'error');
      }
    } catch (error) {
      showToast(`Error ${editingProduct ? 'updating' : 'creating'} product: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    setDeleteConfirm(product);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      const result = await deleteProduct(deleteConfirm.id);
      if (result.success) {
        showToast('Product deleted successfully', 'success');
        setDeleteConfirm(null);
        loadProducts();
      } else {
        showToast('Error deleting product: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error deleting product: ' + error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'published' ? 'draft' : 'published';
    setLoading(true);
    try {
      const result = await updateProduct(product.id, { status: newStatus });
      if (result.success) {
        showToast(`Product ${newStatus === 'published' ? 'published' : 'unpublished'}`, 'success');
        loadProducts();
      } else {
        showToast('Error updating product status', 'error');
      }
    } catch (error) {
      showToast('Error updating product status: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateProduct = async (product) => {
    setLoading(true);
    try {
      const duplicatedData = {
        ...product,
        title: `${product.title} (Copy)`,
        status: 'draft',
      };
      delete duplicatedData.id;
      delete duplicatedData.createdAt;
      delete duplicatedData.updatedAt;
      
      const result = await createProduct(duplicatedData);
      if (result.success) {
        showToast('Product duplicated successfully!', 'success');
        loadProducts();
      } else {
        showToast('Error duplicating product: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error duplicating product: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = () => {
    setIsReordering(true);
  };

  const handleDragEnd = async (event) => {
    setIsReordering(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredProducts.findIndex(p => p.id === active.id);
    const newIndex = filteredProducts.findIndex(p => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newProducts = arrayMove(filteredProducts, oldIndex, newIndex);
    const productOrders = newProducts.map((product, index) => ({
      id: product.id,
      order: index + 1
    }));

    setLoading(true);
    try {
      const result = await reorderProducts(productOrders);
      if (result.success) {
        showToast('Products reordered successfully', 'success');
        loadProducts();
      } else {
        showToast('Error reordering products', 'error');
        loadProducts(); // Reload to reset order
      }
    } catch (error) {
      showToast('Error reordering products: ' + error.message, 'error');
      loadProducts(); // Reload to reset order
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Products</h2>
          <HelpTooltip
            title="Product Management"
            content="Create and manage all your products here. Products can be digital downloads, courses, coaching sessions, memberships, freebies, or external links. Each product can be published, kept as draft, or archived. Products are used in your storefront, funnels, and can be sold directly."
          />
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setShowProductModal(true);
          }}
          className="px-4 py-2.5 sm:py-2 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] text-white rounded-lg hover:shadow-md transition-all flex items-center gap-2 min-h-[48px] text-sm sm:text-base hover-lift button-press"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">New Product</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      <FeatureGuide
        title="Product Management Guide"
        description="Products are the core of your business. Create products to sell digital downloads, courses, coaching sessions, memberships, or free resources. Products can be organized into sections on your storefront and used in sales funnels."
        steps={[
          "Click 'New Product' to create your first product",
          "Upload a thumbnail image (recommended: 800x600px)",
          "Enter a compelling title and detailed description",
          "Set the price (use £0 for free products)",
          "Choose the product type (download, course, coaching, etc.)",
          "Set status to 'Published' to make it visible to customers",
          "Click 'Save Product' to create it",
          "Use the drag handle (⋮⋮) to reorder products",
          "Click the eye icon to quickly publish/unpublish products"
        ]}
        tips={[
          "Use high-quality images for better conversions",
          "Write detailed descriptions highlighting benefits",
          "Start with a few products and expand over time",
          "Use 'Draft' status to work on products before publishing",
          "Digital downloads require a download URL or file upload",
          "Use categories and tags to organize your products",
          "Featured products appear first on your storefront",
          "Drag products by the grip icon (⋮⋮) to reorder them"
        ]}
      />

      <InfoBanner type="info" title="Product Types Explained">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><strong>Digital Download:</strong> PDFs, guides, templates, or any downloadable file. Customers receive instant access after purchase.</li>
          <li><strong>Online Course:</strong> Structured learning content with modules and lessons. Link to courses created in the Courses tab.</li>
          <li><strong>Coaching Session:</strong> One-on-one or group coaching services. Can be linked to your booking system.</li>
          <li><strong>Membership:</strong> Recurring subscription access to exclusive content or community.</li>
          <li><strong>Freebie:</strong> Free resources to build your email list or provide value to your audience.</li>
          <li><strong>External Link:</strong> Link to external products, services, or affiliate offers.</li>
        </ul>
      </InfoBanner>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by title, description, category, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] transition text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <div className="relative">
              <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] transition appearance-none bg-white text-sm"
              >
                <option value="all">All Types</option>
                <option value="digital_download">Downloads</option>
                <option value="online_course">Courses</option>
                <option value="coaching_session">Coaching</option>
                <option value="membership">Memberships</option>
                <option value="freebie">Freebies</option>
                <option value="external_link">External Links</option>
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] transition appearance-none bg-white text-sm"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Sort and Results */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm bg-white"
            >
              <option value="order">Custom Order</option>
              <option value="title">Title (A-Z)</option>
              <option value="price">Price (High-Low)</option>
              <option value="created">Newest First</option>
            </select>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-sm">
            {searchQuery || filterType !== 'all' || filterStatus !== 'all' ? (
              <>
                <span className="text-gray-500">
                  {filteredProducts.length} of {products.length} product{products.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                    setFilterStatus('all');
                  }}
                  className="text-[#c5a059] hover:text-[#b08e4d] font-medium"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <span className="text-gray-500">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {loading && !isReordering ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2 text-lg font-medium">
            {searchQuery || filterType !== 'all' || filterStatus !== 'all' 
              ? 'No products match your filters' 
              : 'No products yet'}
          </p>
          {searchQuery || filterType !== 'all' || filterStatus !== 'all' ? (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterType('all');
                setFilterStatus('all');
              }}
              className="mt-4 px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
            >
              Clear Filters
            </button>
          ) : (
            <button
              onClick={() => {
                setEditingProduct(null);
                setShowProductModal(true);
              }}
              className="mt-4 px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition hover-lift button-press"
            >
              Create Your First Product
            </button>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredProducts.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredProducts.map(product => (
                <SortableProductCard
                  key={product.id}
                  product={product}
                  onEdit={(p) => {
                    setEditingProduct(p);
                    setShowProductModal(true);
                  }}
                  onDelete={handleDeleteProduct}
                  onDuplicate={handleDuplicateProduct}
                  onToggleStatus={handleToggleStatus}
                  isReordering={isReordering}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          product={deleteConfirm}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
          loading={deleting}
        />
      )}

      {showProductModal && (
        <ProductModal 
          product={editingProduct} 
          onSave={handleSaveProduct} 
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
          }} 
          showToast={showToast}
          loading={loading}
          courses={courses}
        />
      )}
    </div>
  );
}

function ProductModal({ product, onSave, onClose, showToast, loading, courses }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'digital_download',
    price: 0,
    currency: 'GBP',
    thumbnail: '',
    status: 'draft',
    downloadUrl: '',
    externalLink: '',
    courseId: '',
    membershipBenefits: '',
    category: '',
    tags: [],
    featured: false,
    metaTitle: '',
    metaDescription: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || '',
        description: product.description || '',
        type: product.type || 'digital_download',
        price: product.price || 0,
        currency: product.currency || 'GBP',
        thumbnail: product.thumbnail || '',
        status: product.status || 'draft',
        downloadUrl: product.downloadUrl || '',
        externalLink: product.externalLink || '',
        courseId: product.courseId || '',
        membershipBenefits: product.membershipBenefits || '',
        category: product.category || '',
        tags: product.tags || [],
        featured: product.featured || false,
        metaTitle: product.metaTitle || '',
        metaDescription: product.metaDescription || '',
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (url) => {
    setFormData(prev => ({ ...prev, thumbnail: url }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('Product title is required', 'error');
      return;
    }
    if (formData.type === 'digital_download' && !formData.downloadUrl.trim()) {
      showToast('Download URL is required for digital downloads', 'error');
      return;
    }
    if (formData.type === 'external_link' && !formData.externalLink.trim()) {
      showToast('External link is required for external link products', 'error');
      return;
    }
    if (formData.type === 'online_course' && !formData.courseId) {
      showToast('Please select a course to link', 'error');
      return;
    }
    // Validate course exists if linking to a course
    if (formData.type === 'online_course' && formData.courseId) {
      try {
        const course = await getCourse(formData.courseId);
        if (!course) {
          showToast('Selected course does not exist. Please select a valid course.', 'error');
          return;
        }
      } catch (error) {
        showToast('Error validating course. Please try again.', 'error');
        return;
      }
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-none sm:rounded-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10 flex items-center justify-between p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-serif text-gray-800">{product ? 'Edit Product' : 'Create New Product'}</h2>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 sm:bg-white rounded-full hover:bg-gray-200 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-4 sm:px-6 overflow-x-auto">
          <div className="flex gap-1 min-w-max sm:min-w-0">
            {[
              { id: 'basic', label: 'Basic Info' },
              { id: 'details', label: 'Details' },
              { id: 'seo', label: 'SEO' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#c5a059] text-[#c5a059]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 pb-20 sm:pb-6">
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <ImageUpload 
                  value={formData.thumbnail} 
                  onChange={handleImageChange}
                  label="Product Thumbnail"
                />

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title *</label>
                    <HelpTooltip
                      title="Product Title"
                      content="Choose a clear, compelling title that describes what your product is. This appears on your storefront, in funnels, and in checkout. Keep it concise but descriptive (50-60 characters is ideal)."
                    />
                  </div>
                  <div className="flex items-center border-2 border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-[#c5a059] focus-within:border-[#c5a059] transition-all duration-200">
                    <Type size={18} className="text-gray-400 ml-3 flex-shrink-0" />
                    <input
                      type="text"
                      id="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="flex-1 px-4 py-2.5 bg-transparent outline-none"
                      placeholder="e.g. 'Self-Love Workbook' or 'Boundary Setting Course'"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                    <HelpTooltip
                      title="Product Description"
                      content="Write a detailed description that explains what the product is, who it's for, and what benefits customers will get. Use bullet points, highlight key features, and include what's included. This helps customers understand the value and increases conversions."
                    />
                  </div>
                  <div className="flex items-start border-2 border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-[#c5a059] focus-within:border-[#c5a059] transition-all duration-200">
                    <AlignLeft size={18} className="text-gray-400 ml-3 mt-3 flex-shrink-0" />
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="6"
                      className="flex-1 px-4 py-2.5 bg-transparent outline-none resize-none"
                      placeholder="Describe what this product includes, who it's for, and what benefits customers will receive. Be specific and highlight the value..."
                    ></textarea>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Tip: Include what's inside, who it's for, and what results they can expect</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700">Price</label>
                      <HelpTooltip
                        title="Product Price"
                        content="Set the price in GBP (£). Use 0 for free products. Prices can be changed later. For payment plans or subscriptions, set the base price here."
                      />
                    </div>
                    <div className="flex items-center border-2 border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-[#c5a059] focus-within:border-[#c5a059] transition-all duration-200">
                      <DollarSign size={18} className="text-gray-400 ml-3 flex-shrink-0" />
                      <input
                        type="number"
                        id="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="flex-1 px-4 py-2.5 bg-transparent outline-none"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter 0 for free products</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                      <HelpTooltip
                        title="Product Status"
                        content="Draft: Product is saved but not visible to customers. Published: Product appears on your storefront and can be purchased. Archived: Product is hidden but not deleted (useful for seasonal products)."
                      />
                    </div>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    >
                      <option value="draft">Draft (Not visible)</option>
                      <option value="published">Published (Live)</option>
                      <option value="archived">Archived (Hidden)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">Product Type</label>
                    <HelpTooltip
                      title="Product Type"
                      content="Choose the type that best matches your product. This determines how it's displayed and what features are available. You can change this later if needed."
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { id: 'digital_download', label: 'Download', icon: Download },
                      { id: 'online_course', label: 'Course', icon: BookOpen },
                      { id: 'coaching_session', label: 'Coaching', icon: Users },
                      { id: 'membership', label: 'Membership', icon: Users },
                      { id: 'freebie', label: 'Freebie', icon: Gift },
                      { id: 'external_link', label: 'Link', icon: Link },
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: type.id })}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all ${
                          formData.type === type.id
                            ? 'border-[#c5a059] bg-[#fdfbf7] text-[#c5a059] shadow-sm'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <type.icon size={20} />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="space-y-4">
                {/* Type-specific fields */}
                {formData.type === 'digital_download' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="downloadUrl" className="block text-sm font-medium text-gray-700">Download URL *</label>
                      <HelpTooltip
                        title="Download URL"
                        content="Enter the URL where customers can download the file after purchase. This can be a direct link to a PDF, ZIP file, or any downloadable resource. Make sure the URL is publicly accessible or use a secure download system."
                      />
                    </div>
                    <input
                      type="url"
                      id="downloadUrl"
                      value={formData.downloadUrl}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                      placeholder="https://example.com/file.pdf"
                      required={formData.type === 'digital_download'}
                    />
                  </div>
                )}

                {formData.type === 'external_link' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="externalLink" className="block text-sm font-medium text-gray-700">External Link *</label>
                      <HelpTooltip
                        title="External Link"
                        content="Enter the URL where customers will be redirected when they click on this product. This is useful for affiliate links, external services, or products hosted elsewhere."
                      />
                    </div>
                    <input
                      type="url"
                      id="externalLink"
                      value={formData.externalLink}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                      placeholder="https://example.com/product"
                      required={formData.type === 'external_link'}
                    />
                  </div>
                )}

                {formData.type === 'online_course' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="courseId" className="block text-sm font-medium text-gray-700">Link to Course *</label>
                      <HelpTooltip
                        title="Course Link"
                        content="Select a course from your Courses tab to link to this product. When customers purchase this product, they'll get access to the selected course."
                      />
                    </div>
                    <select
                      id="courseId"
                      value={formData.courseId}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                      required={formData.type === 'online_course'}
                    >
                      <option value="">Select a course...</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>{course.title}</option>
                      ))}
                    </select>
                    {courses.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No courses available. Create a course in the Courses tab first.</p>
                    )}
                  </div>
                )}

                {formData.type === 'membership' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="membershipBenefits" className="block text-sm font-medium text-gray-700">Membership Benefits</label>
                      <HelpTooltip
                        title="Membership Benefits"
                        content="List the key benefits and features of this membership. This helps customers understand what they'll get access to. Use bullet points or short descriptions."
                      />
                    </div>
                    <textarea
                      id="membershipBenefits"
                      value={formData.membershipBenefits}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all resize-none"
                      placeholder="• Exclusive content access&#10;• Monthly live sessions&#10;• Private community access&#10;• Early access to new products"
                    />
                  </div>
                )}

                {/* Category and Tags */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                      <HelpTooltip
                        title="Product Category"
                        content="Organize your products into categories (e.g., 'Workbooks', 'Courses', 'Coaching'). This helps customers find related products and helps you organize your storefront."
                      />
                    </div>
                    <input
                      type="text"
                      id="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                      placeholder="e.g. Workbooks, Courses"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-medium text-gray-700">Tags</label>
                      <HelpTooltip
                        title="Product Tags"
                        content="Add tags to help customers find products by topic, theme, or feature. Tags are searchable and help with product discovery."
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                        placeholder="Add a tag and press Enter..."
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
                        title="Add tag"
                      >
                        <Hash size={18} />
                      </button>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-sm"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Featured Product */}
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-2 border-amber-200">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="w-5 h-5 text-[#c5a059] border-gray-300 rounded focus:ring-[#c5a059]"
                  />
                  <div className="flex-1">
                    <label htmlFor="featured" className="text-sm font-medium text-gray-700 cursor-pointer flex items-center gap-2">
                      <Star size={16} className="text-[#c5a059] fill-[#c5a059]" />
                      Featured Product
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">Featured products appear first on your storefront</p>
                  </div>
                  <HelpTooltip
                    title="Featured Products"
                    content="Featured products are highlighted and appear at the top of your storefront. Use this for your best-selling or most important products."
                  />
                </div>
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="metaTitle" className="block text-sm font-medium text-gray-700">Meta Title</label>
                    <HelpTooltip
                      title="Meta Title"
                      content="The title that appears in search engine results and browser tabs. If left empty, the product title will be used. Keep it under 60 characters for best results."
                    />
                  </div>
                  <input
                    type="text"
                    id="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    maxLength={60}
                    className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="SEO-friendly title (optional)"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">{formData.metaTitle.length}/60 characters</p>
                    {formData.metaTitle.length > 60 && (
                      <p className="text-xs text-red-500">Too long for search results</p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700">Meta Description</label>
                    <HelpTooltip
                      title="Meta Description"
                      content="A brief description that appears in search engine results. If left empty, the product description will be used. Keep it under 160 characters for best results."
                    />
                  </div>
                  <textarea
                    id="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    rows="3"
                    maxLength={160}
                    className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all resize-none"
                    placeholder="SEO-friendly description (optional)"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">{formData.metaDescription.length}/160 characters</p>
                    {formData.metaDescription.length > 160 && (
                      <p className="text-xs text-red-500">Too long for search results</p>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">SEO Tips:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Include your main keyword in the meta title</li>
                        <li>Write compelling descriptions that encourage clicks</li>
                        <li>If left empty, your product title and description will be used</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 sm:p-6 flex gap-3 sm:gap-4 shadow-lg sm:shadow-none">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border-2 border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition font-medium min-h-[48px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 px-4 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition font-medium shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 min-h-[48px]"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span className="hidden sm:inline">Saving...</span>
                  <span className="sm:hidden">Save</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} className="hidden sm:block" />
                  <span>Save Product</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
